import {
  SearchOutlined,
  UnlockOutlined
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { PERMISSIONS } from "../../constants/permissions";
import { getBlockedIps, unblockIp } from "../../services/blockedIpService";
import { RootState } from "../../store";
import { BlockedIp, BlockedIpFilters } from "../../types/blockedIp";

const { Title, Text } = Typography;

// Helper function for formatting dates
const formatDateTime = (
  dateString: string | null | undefined,
  format = 'YYYY-MM-DD HH:mm:ss'
): string => {
  if (!dateString) return 'N/A';
  const date = dayjs(dateString);
  return date.isValid() ? date.format(format) : 'N/A';
};

const BlockedIpsList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BlockedIp[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState<BlockedIpFilters>({
    active: true,
  });
  const [searchForm] = Form.useForm();
  const { permissions } = useSelector((state: RootState) => state.auth);

  const fetchBlockedIps = async (
    page = pagination.current - 1,
    size = pagination.pageSize,
    searchFilters = filters
  ) => {
    setLoading(true);
    try {
      const response = await getBlockedIps(page, size, searchFilters);
      const paginatedData = response.data.data;
      setData(paginatedData.content);
      setPagination({
        ...pagination,
        current: paginatedData.pageNumber + 1,
        pageSize: paginatedData.pageSize,
        total: paginatedData.totalElements,
      });
    } catch (error) {
      console.error("Error fetching blocked IPs:", error);
      message.error("Failed to fetch blocked IPs");
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
    fetchBlockedIps(pagination.current - 1, pagination.pageSize);
  };

  useEffect(() => {
    fetchBlockedIps();
  }, []);

  const handleSearch = (values: any) => {
    const newFilters: BlockedIpFilters = {
      ...filters,
      ipAddress: values.ipAddress || undefined,
      username: values.username || undefined,
    };
    setFilters(newFilters);
    fetchBlockedIps(0, pagination.pageSize, newFilters);
  };

  const handleReset = () => {
    searchForm.resetFields();
    const newFilters: BlockedIpFilters = {
      active: filters.active,
    };
    setFilters(newFilters);
    fetchBlockedIps(0, pagination.pageSize, newFilters);
  };

  const handleUnblockIp = async (record: BlockedIp) => {
    try {
      await unblockIp(record.ipAddress);
      message.success(`IP ${record.ipAddress} has been unblocked successfully`);
      fetchBlockedIps();
    } catch (error) {
      console.error("Error unblocking IP:", error);
      message.error("Failed to unblock IP");
    }
  };

  const hasPermission = (permissionKey: string) => {
    return permissions.some((p: any) => p.name === permissionKey);
  };

  const columns = [
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      key: "ipAddress",
      width: 150,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Blocked At",
      dataIndex: "blockedAt",
      key: "blockedAt",
      width: 180,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: "Unblock At",
      dataIndex: "unblockAt",
      key: "unblockAt",
      width: 180,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: "Failed Attempts",
      key: "attempts",
      width: 220,
      render: (record: BlockedIp) => (
        <Space direction="vertical" size="small">
          <Text>Total: {record.failedAttempts}</Text>
          <Space>
            <Tag color="red">Login: {record.failedLoginAttempts || 0}</Tag>
            <Tag color="orange">
              Unauth: {record.failedUnauthenticatedAttempts || 0}
            </Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: 120,
      render: (active: boolean) => (
        <Tag color={active ? "red" : "green"}>
          {active ? "Blocked" : "Unblocked"}
        </Tag>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>{text || "N/A"}</Tooltip>
      ),
    },
    {
      title: "Last Failure",
      dataIndex: "lastFailureType",
      key: "lastFailureType",
      width: 120,
      render: (type: string) => (
        <Tag color={type === "LOGIN" ? "blue" : "purple"}>
          {type || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      render: (record: BlockedIp) => (
        hasPermission(PERMISSIONS.UNBLOCK_IP.name) && record.active ? (
          <Popconfirm
            title="Are you sure you want to unblock this IP?"
            onConfirm={() => handleUnblockIp(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              size="small"
              icon={<UnlockOutlined />}
            >
              Unblock
            </Button>
          </Popconfirm>
        ) : null
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>Blocked IPs</Title>
            <Text type="secondary">View and manage blocked IP addresses</Text>
          </div>
        </div>
        <Divider style={{ margin: '16px 0' }} />
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Form
          form={searchForm}
          layout="vertical"
          onFinish={handleSearch}
          initialValues={filters}
        >
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="ipAddress" label="IP Address">
                <Input placeholder="Filter by IP address" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="username" label="Username">
                <Input placeholder="Filter by username" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} style={{ display: "flex", alignItems: "flex-end" }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  Search
                </Button>
                <Button onClick={handleReset}>Reset</Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>
    </div>
  );
};

export default BlockedIpsList; 